import { getAnyInitData, getInitDataWsParam, isMiniAppContext, } from "./miniApp";
// Persist-дедуп показанных тостов: ws_event_id, для которых тост уже
// показывался, не тостятся повторно даже при перезагрузке/реконнекте.
const TOASTED_IDS_STORAGE_KEY = "wsToastedEventIds";
const TOASTED_IDS_CAP = 500;
const readToastedIds = () => {
    try {
        const raw = localStorage.getItem(TOASTED_IDS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
    }
    catch {
        return [];
    }
};
const writeToastedIds = (ids) => {
    try {
        localStorage.setItem(TOASTED_IDS_STORAGE_KEY, JSON.stringify(ids));
    }
    catch {
        /* приватный режим / переполнение — дедуп деградирует, не критично */
    }
};
class WebSocketManager {
    constructor() {
        this.ws = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.handlers = new Map();
        // Глобальные слушатели ВСЕХ уведомлений (в дополнение к роутингу по
        // ws_event_id). Нужны для пассивных консьюмеров по ТИПУ события — напр.
        // список «Моя музыка» ловит готовность минуса, даже если активной подписки
        // waitForInstrumental нет (перезагрузка страницы / другая вкладка).
        this.globalHandlers = new Set();
        this.isConnecting = false;
        this.shouldMaintainConnection = false; // Флаг для поддержания соединения
        // Слив стартового бэклога. После каждого onopen backend сразу отдаёт
        // накопленные PENDING-уведомления бурстом — пока идёт слив, помечаем события
        // как backlog (без тоста). Таймер продлевается на каждое сообщение (debounce),
        // поэтому окно закрывается только когда бурст реально закончился.
        this.isDrainingBacklog = false;
        this.backlogDrainTimer = null;
        // ws_event_id, для которых тост уже показывался (persist в localStorage).
        this.toastedIds = new Set(readToastedIds());
        // Получаем базовый URL из переменной окружения или используем текущий хост
        const apiUrl = process.env.REACT_APP_API_URL || "";
        if (apiUrl) {
            // Преобразуем HTTP URL в WebSocket URL
            // Убираем trailing slash, если есть
            let cleanUrl = apiUrl.replace(/\/$/, "");
            // Для HTTPS используем WSS, для HTTP - WS
            this.baseUrl = cleanUrl.replace(/^https/, "wss").replace(/^http/, "ws");
            console.log("WebSocket baseUrl:", this.baseUrl, "from API URL:", apiUrl);
        }
        else {
            // Если нет API URL, используем текущий хост
            const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
            this.baseUrl = `${protocol}//${window.location.host}`;
            console.log("WebSocket baseUrl (fallback):", this.baseUrl);
        }
    }
    /**
     * Подключается к WebSocket серверу
     * @param maintainConnection - Если true, соединение будет поддерживаться даже без активных подписок
     */
    connect(maintainConnection = false) {
        return new Promise((resolve, reject) => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }
            // Устанавливаем флаг поддержания соединения
            if (maintainConnection) {
                this.shouldMaintainConnection = true;
            }
            if (this.isConnecting) {
                // Ждем, пока подключение завершится
                const checkConnection = setInterval(() => {
                    if (this.ws?.readyState === WebSocket.OPEN) {
                        clearInterval(checkConnection);
                        resolve();
                    }
                    else if (!this.isConnecting) {
                        clearInterval(checkConnection);
                        reject(new Error("Failed to connect"));
                    }
                }, 100);
                return;
            }
            this.isConnecting = true;
            try {
                // Проверяем, находимся ли мы в мини-приложении (Telegram или MAX)
                const isMiniApp = isMiniAppContext();
                // Получаем initData текущей платформы (TG/MAX)
                const initData = getAnyInitData();
                const accessToken = localStorage.getItem("accessToken");
                // Формируем URL для WebSocket
                // WebSocket endpoint находится по пути /public/notifications
                let wsUrl = `${this.baseUrl}/public/notifications`;
                const params = [];
                // initData передаём query-параметром, имя которого зависит от платформы:
                // Telegram → tg_init_data, MAX → max_init_data (см. getInitDataWsParam).
                const initDataParam = getInitDataWsParam();
                if (initDataParam) {
                    params.push(initDataParam);
                }
                // Токен из localStorage (мини-приложения и обратная совместимость веба).
                // Для веба авторизация обычно на cookie, которые браузер шлёт сам при
                // подключении к тому же домену.
                if (accessToken) {
                    params.push(`token=${encodeURIComponent(accessToken)}`);
                }
                if (params.length > 0) {
                    wsUrl += `?${params.join("&")}`;
                }
                console.log("Connecting to WebSocket:", wsUrl);
                console.log("Is MiniApp:", isMiniApp);
                console.log("Has initData:", !!initData);
                console.log("Has accessToken:", !!accessToken);
                this.ws = new WebSocket(wsUrl);
                let connectionResolved = false;
                let connectionRejected = false;
                const resolveConnection = () => {
                    if (!connectionResolved && !connectionRejected) {
                        connectionResolved = true;
                        this.isConnecting = false;
                        this.reconnectAttempts = 0;
                        resolve();
                    }
                };
                const rejectConnection = (error) => {
                    if (!connectionResolved && !connectionRejected) {
                        connectionRejected = true;
                        this.isConnecting = false;
                        this.ws = null;
                        reject(error);
                    }
                };
                this.ws.onopen = () => {
                    console.log("WebSocket connected to:", wsUrl);
                    // Начинаем окно слива стартового бэклога — до его закрытия входящие
                    // события считаются backlog (без тоста).
                    this.beginBacklogDrain();
                    resolveConnection();
                };
                this.ws.onmessage = (event) => {
                    try {
                        const notification = JSON.parse(event.data);
                        console.log("WebSocket message received:", notification);
                        this.handleNotification(notification);
                    }
                    catch (error) {
                        console.error("Error parsing WebSocket message:", error);
                    }
                };
                this.ws.onerror = (error) => {
                    console.error("WebSocket error:", error);
                    // Не вызываем reject здесь, так как onclose тоже может быть вызван
                    // и там будет более полная информация об ошибке
                };
                this.ws.onclose = (event) => {
                    console.log("WebSocket disconnected. Code:", event.code, "Reason:", event.reason || "No reason provided");
                    console.log("Was clean:", event.wasClean);
                    this.isConnecting = false;
                    // Если подключение еще не было установлено (onopen не был вызван),
                    // это означает ошибку подключения
                    if (!connectionResolved && !connectionRejected) {
                        let errorMessage = `WebSocket connection failed. Code: ${event.code}`;
                        if (event.reason) {
                            errorMessage += `, Reason: ${event.reason}`;
                        }
                        if (event.code === 1006) {
                            errorMessage += " (Connection closed abnormally. Possible causes: server not available, authentication required, or CORS issue)";
                        }
                        rejectConnection(new Error(errorMessage));
                    }
                    this.ws = null;
                    // Пытаемся переподключиться только если подключение было установлено ранее
                    // и если есть активные подписки или нужно поддерживать соединение
                    if (connectionResolved &&
                        this.reconnectAttempts < this.maxReconnectAttempts &&
                        (this.handlers.size > 0 || this.shouldMaintainConnection)) {
                        this.reconnectAttempts++;
                        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                        setTimeout(() => {
                            this.connect(this.shouldMaintainConnection).catch(() => {
                                // Игнорируем ошибки переподключения
                            });
                        }, this.reconnectDelay * this.reconnectAttempts);
                    }
                    else if (connectionResolved) {
                        // Сбрасываем счетчик попыток, если не нужно переподключаться
                        this.reconnectAttempts = 0;
                    }
                };
            }
            catch (error) {
                this.isConnecting = false;
                reject(error);
            }
        });
    }
    /**
     * Отключается от WebSocket сервера
     */
    disconnect() {
        this.shouldMaintainConnection = false;
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.handlers.clear();
        this.reconnectAttempts = 0;
        if (this.backlogDrainTimer) {
            clearTimeout(this.backlogDrainTimer);
            this.backlogDrainTimer = null;
        }
        this.isDrainingBacklog = false;
    }
    /** Открывает окно слива стартового бэклога (вызывается на каждый onopen). */
    beginBacklogDrain() {
        this.isDrainingBacklog = true;
        this.armBacklogDrainTimer();
    }
    /** Продлевает окно слива бэклога (debounce на каждое входящее сообщение). */
    armBacklogDrainTimer() {
        if (this.backlogDrainTimer) {
            clearTimeout(this.backlogDrainTimer);
        }
        this.backlogDrainTimer = setTimeout(() => {
            this.isDrainingBacklog = false;
            this.backlogDrainTimer = null;
        }, WebSocketManager.BACKLOG_DRAIN_MS);
    }
    rememberToasted(wsEventId) {
        if (this.toastedIds.has(wsEventId))
            return;
        this.toastedIds.add(wsEventId);
        // Держим в localStorage ограниченный хвост (последние N id).
        let ids = readToastedIds();
        ids.push(wsEventId);
        if (ids.length > TOASTED_IDS_CAP) {
            ids = ids.slice(ids.length - TOASTED_IDS_CAP);
            this.toastedIds = new Set(ids);
        }
        writeToastedIds(ids);
    }
    /**
     * Подписывается на уведомления по ws_event_id
     */
    subscribe(wsEventId, handler) {
        this.handlers.set(wsEventId, handler);
        // Убеждаемся, что подключение установлено (с тем же режимом, что и основная сессия)
        if (this.ws?.readyState !== WebSocket.OPEN) {
            this.connect(this.shouldMaintainConnection).catch((error) => {
                console.error("Failed to connect WebSocket for subscription:", error);
            });
        }
    }
    /**
     * Отписывается от уведомлений по ws_event_id
     */
    unsubscribe(wsEventId) {
        this.handlers.delete(wsEventId);
    }
    /**
     * Глобальный слушатель ВСЕХ уведомлений (роутинг по типу делает сам
     * потребитель). Возвращает функцию отписки. Используется для пассивной
     * синхронизации по типу события без активной подписки на конкретный
     * ws_event_id (напр. готовность минуса после перезагрузки страницы).
     */
    subscribeAll(handler) {
        this.globalHandlers.add(handler);
        if (this.ws?.readyState !== WebSocket.OPEN) {
            this.connect(this.shouldMaintainConnection).catch((error) => {
                console.error("Failed to connect WebSocket for global subscription:", error);
            });
        }
        return () => {
            this.globalHandlers.delete(handler);
        };
    }
    /**
     * Обрабатывает входящее уведомление
     */
    handleNotification(notification) {
        // Проверяем, что это уведомление из канала notifications
        if (notification.channel !== "notifications") {
            return;
        }
        const wsEventId = notification.payload?.ws_event_id;
        // Классификация backlog vs live для решения «тостить или нет».
        //   - идёт слив стартового бэклога → backlog;
        //   - ws_event_id уже показывался ранее (persist-дедуп) → backlog;
        //   - иначе live → помечаем id как показанный, чтобы не тостить повторно.
        let isBacklog = this.isDrainingBacklog;
        if (this.isDrainingBacklog) {
            // Ещё сообщение в бурсте — продлеваем окно слива.
            this.armBacklogDrainTimer();
        }
        if (wsEventId) {
            if (this.toastedIds.has(wsEventId)) {
                isBacklog = true;
            }
            else if (!isBacklog) {
                this.rememberToasted(wsEventId);
            }
        }
        const meta = { isBacklog };
        // Роутинг по ws_event_id (активные подписки waitFor*).
        const handler = wsEventId ? this.handlers.get(wsEventId) : undefined;
        if (handler) {
            handler(notification, meta);
        }
        else if (!wsEventId) {
            console.warn("WebSocket notification missing ws_event_id in payload:", notification);
        }
        // Глобальные слушатели (пассивная синхронизация по типу события) —
        // получают уведомление ВСЕГДА, независимо от наличия ws_event_id-подписки.
        this.globalHandlers.forEach((h) => {
            try {
                h(notification, meta);
            }
            catch (e) {
                console.error("WebSocket global handler failed:", e);
            }
        });
    }
    /**
     * Проверяет, подключен ли WebSocket
     */
    isConnected() {
        return this.ws?.readyState === WebSocket.OPEN;
    }
}
WebSocketManager.BACKLOG_DRAIN_MS = 800;
// Создаем единственный экземпляр менеджера
export const wsManager = new WebSocketManager();
